import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PaginationBar } from "@/components/PaginationBar";
import { AuditCell } from "@/components/AuditCell";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatCurrency, formatDate } from "@/lib/format";

type Bill = components["schemas"]["BillResponse"];
type BillState = components["schemas"]["BillState"];

export interface BillsTableFilters {
  states?: BillState[];
  invoiceNumber?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
}

interface BillsTableProps {
  organisationId?: string;
  /** Page-owned filters (kept in the URL). Without them the table renders its own invoice-number box. */
  filters?: BillsTableFilters;
}

const BASE_COLUMNS = 10;

function stateVariant(state: string | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (state) {
    case "Paid":
      return "default";
    case "Approved":
    case "Scheduled":
      return "secondary";
    case "Rejected":
      return "destructive";
    default:
      return "outline";
  }
}

function formatConfidence(value: Bill["confidence"]): { label: string; tone: string } | null {
  if (value === undefined || value === null) return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return null;
  const pct = num <= 1 ? num * 100 : num;
  const tone =
    pct >= 90 ? "text-emerald-600 dark:text-emerald-400"
    : pct >= 70 ? "text-amber-600 dark:text-amber-400"
    : "text-destructive";
  return { label: `${pct.toFixed(0)}%`, tone };
}

function BillFileDownloadButton({ billId, fileName }: { billId: string; fileName?: string | null }) {
  const [loading, setLoading] = useState(false);

  // Signed URLs are short-lived, so fetch one at click time rather than for every row up front.
  async function handleDownload() {
    setLoading(true);
    const { data, error } = await api.GET("/admin/bills/{id}/file-url", {
      params: { path: { id: billId } },
    });
    setLoading(false);
    if (error || !data?.url) {
      toast.error("No file is available for this bill.");
      return;
    }
    const link = document.createElement("a");
    link.href = data.url;
    link.download = fileName ?? "";
    link.target = "_blank";
    link.rel = "noreferrer noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          disabled={loading}
          onClick={handleDownload}
          aria-label="Download bill document"
        >
          {loading ? <Spinner className="size-4" /> : <DownloadIcon className="size-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Download document</TooltipContent>
    </Tooltip>
  );
}

export function BillsTable({ organisationId, filters }: BillsTableProps) {
  const [invoiceNumberInput, setInvoiceNumberInput] = useState("");
  const debouncedInvoiceNumberInput = useDebouncedValue(invoiceNumberInput);
  const debouncedInvoiceNumber = filters ? (filters.invoiceNumber ?? "") : debouncedInvoiceNumberInput;
  const states = filters?.states?.join(",");
  const invoiceDateFrom = filters?.invoiceDateFrom;
  const invoiceDateTo = filters?.invoiceDateTo;

  const showOrganisation = !organisationId;
  const columnCount = BASE_COLUMNS + (showOrganisation ? 1 : 0);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const listKey = [organisationId, debouncedInvoiceNumber, states, invoiceDateFrom, invoiceDateTo].join("|");
  const prevListKeyRef = useRef(listKey);
  useEffect(() => {
    if (prevListKeyRef.current !== listKey) {
      prevListKeyRef.current = listKey;
      pagination.reset();
    }
  }, [listKey, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchBills() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/bills", {
        params: {
          query: {
            OrganisationId: organisationId,
            InvoiceNumber: debouncedInvoiceNumber || undefined,
            States: states ? (states.split(",") as BillState[]) : undefined,
            InvoiceDateFrom: invoiceDateFrom,
            InvoiceDateTo: invoiceDateTo,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load bills.");
      } else {
        setBills(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchBills();
    return () => {
      cancelled = true;
    };
  }, [
    organisationId,
    pagination.cursor,
    pagination.limit,
    debouncedInvoiceNumber,
    states,
    invoiceDateFrom,
    invoiceDateTo,
  ]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      {filters ? (
        <div className="mb-2 flex h-5 items-center text-sm text-muted-foreground">
          {loading && <Spinner className="size-4" />}
        </div>
      ) : (
        <div className="mb-4 flex items-center gap-3">
          <Input
            placeholder="Filter by invoice number..."
            value={invoiceNumberInput}
            onChange={(e) => setInvoiceNumberInput(e.target.value)}
            className="max-w-sm"
          />
          {loading && <Spinner className="size-5" />}
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Invoice #</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Supplier</TableHead>
              {showOrganisation && <TableHead>Organisation</TableHead>}
              <TableHead>Total</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Last modified</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Document</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bills.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="text-muted-foreground h-24 text-center"
                >
                  No bills found.
                </TableCell>
              </TableRow>
            ) : (
              bills.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>
                    {bill.id ? (
                      <Link to={`/bills/${bill.id}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {bill.id.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono font-medium">
                    {bill.invoiceNumber ? (
                      bill.id ? (
                        <Link to={`/bills/${bill.id}`} className="hover:underline">
                          {bill.invoiceNumber}
                        </Link>
                      ) : (
                        bill.invoiceNumber
                      )
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Badge variant={stateVariant(bill.state)}>{bill.state}</Badge>
                      {/* Only while unresolved: a confirmed non-duplicate keeps its link but isn't stuck. */}
                      {bill.duplicateOfBillId && !bill.duplicateDismissedAt && (
                        <Badge
                          className="border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                          title="Possible duplicate of an earlier bill — approval blocked until confirmed"
                        >
                          Dup
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {bill.source ? (
                      <Badge variant="outline">{bill.source}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const confidence = formatConfidence(bill.confidence);
                      return confidence ? (
                        <span className={`font-mono text-xs ${confidence.tone}`}>{confidence.label}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {bill.supplierId ? (
                      <Link to={`/suppliers/${bill.supplierId}`} className="hover:underline">
                        {bill.supplier?.name || bill.supplierName || bill.supplierId.slice(0, 8) + "..."}
                      </Link>
                    ) : (
                      bill.supplier?.name || bill.supplierName || (
                        <span className="text-muted-foreground">—</span>
                      )
                    )}
                  </TableCell>
                  {showOrganisation && (
                    <TableCell>
                      {bill.organisationId ? (
                        <Link to={`/organisations/${bill.organisationId}`}>
                          <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                            {bill.organisationId.slice(0, 8)}...
                          </Badge>
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {formatCurrency(bill.totalAmount, bill.currency ?? "GBP")}
                  </TableCell>
                  <TableCell>{formatDate(bill.dueDate)}</TableCell>
                  <TableCell>
                    <AuditCell
                      createdAt={bill.createdAt ?? ""}
                      createdBy={bill.createdBy}
                      updatedAt={bill.updatedAt ?? ""}
                      updatedBy={bill.updatedBy}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {bill.id && <BillFileDownloadButton billId={bill.id} fileName={bill.fileName} />}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
